import { PrivateAxiosInstance } from '@/api/axios'
import { endPoint } from '@/constant/end-point'
import { useCallback, useEffect, useRef, useState } from 'react'

export type AssistantRole = 'user' | 'assistant'

export type AssistantContentBlock =
  | { type: 'text'; text: string }
  | {
      type: 'tool_use'
      id: string
      name: string
      input: Record<string, unknown>
      resolvedPath?: string
      status: 'pending' | 'navigated' | 'denied'
    }
  | {
      type: 'tool_result'
      tool_use_id: string
      content: string
      is_error?: boolean
    }

export type AssistantReaction = 'LIKE' | 'DISLIKE' | null

export interface AssistantMessage {
  id: string
  role: AssistantRole
  blocks: AssistantContentBlock[]
  pending?: boolean
  errored?: boolean
  reaction?: AssistantReaction
}

export interface ConversationSummary {
  id: string
  title: string | null
  updatedAt: string
  createdAt: string
}

export type AssistantErrorCode = 'quota_exceeded' | 'unauthorized' | 'unknown'

export interface AssistantBanner {
  code: AssistantErrorCode
  message: string
}

interface SendMessageParams {
  text: string
  currentPath?: string
}

interface DispatchToolUseParams {
  toolUseId: string
  navigate: (path: string) => void
  currentPath?: string
}

const STREAM_URL = endPoint.assistant.chatStream
const CONVERSATIONS_URL = endPoint.assistant.conversations
const MESSAGE_REACTION_URL = endPoint.assistant.messageReaction
const newId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`

const parseSseChunk = (chunk: string, onEvent: (event: string, data: string) => void) => {
  const blocks = chunk.split('\n\n')
  for (const block of blocks) {
    if (!block.trim()) continue
    let event = 'message'
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (dataLines.length > 0) onEvent(event, dataLines.join('\n'))
  }
}

const stripBlockStateFields = (b: AssistantContentBlock) => {
  if (b.type === 'tool_use') {
    const { type, id, name, input } = b
    return { type, id, name, input }
  }
  return b
}

const buildQueryString = (entries: [string, string][]) => {
  if (entries.length === 0) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of entries) usp.append(k, v)
  return `?${usp.toString()}`
}

const navigateInputToQueryEntries = (input: Record<string, unknown>): [string, string][] => {
  const query = input.query as Record<string, unknown> | undefined
  if (!query) return []
  return Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => [k, String(v)])
}

const openAddModalInputToQueryEntries = (input: Record<string, unknown>): [string, string][] => {
  const entries: [string, string][] = [['assistantOpenAdd', '1']]
  const prefill = input.prefill as Record<string, unknown> | undefined
  if (prefill && Object.keys(prefill).length > 0) {
    try {
      entries.push(['assistantPrefill', JSON.stringify(prefill)])
    } catch {
      /* ignore */
    }
  }
  return entries
}

interface PersistedDbBlock {
  type?: string
  text?: string
  id?: string
  name?: string
  input?: Record<string, unknown>
  resolvedPath?: string
  status?: 'pending' | 'navigated' | 'denied'
  tool_use_id?: string
  content?: string
  is_error?: boolean
}

interface PersistedDbMessage {
  id: string
  role: string
  blocks: PersistedDbBlock[] | unknown
  reaction?: string | null
  createdAt: string
}

// Scan the entire persisted history for tool_result blocks and index them by
// the tool_use_id they answer. Used to decide whether a hydrated tool_use
// has already been acted on (button should be disabled) vs. still
// outstanding (button should remain clickable on a later visit).
const indexToolResultsById = (raw: PersistedDbMessage[]): Map<string, { isError: boolean }> => {
  const acted = new Map<string, { isError: boolean }>()
  for (const m of raw) {
    if (!Array.isArray(m.blocks)) continue
    for (const b of m.blocks as PersistedDbBlock[]) {
      if (b?.type === 'tool_result' && typeof b.tool_use_id === 'string') {
        acted.set(b.tool_use_id, { isError: !!b.is_error })
      }
    }
  }
  return acted
}

const hydrateBlocks = (raw: unknown, acted: Map<string, { isError: boolean }>): AssistantContentBlock[] => {
  if (!Array.isArray(raw)) return []
  const out: AssistantContentBlock[] = []
  for (const b of raw as PersistedDbBlock[]) {
    if (!b || typeof b !== 'object') continue
    if (b.type === 'text' && typeof b.text === 'string') {
      out.push({ type: 'text', text: b.text })
    } else if (b.type === 'tool_use' && typeof b.id === 'string' && typeof b.name === 'string') {
      const resolvedPath = typeof b.resolvedPath === 'string' && b.resolvedPath.length > 0 ? b.resolvedPath : undefined
      const priorResult = acted.get(b.id)
      let status: 'pending' | 'navigated' | 'denied'
      if (b.status === 'navigated' || b.status === 'denied') {
        // Late click recorded on the tool_use itself (no adjacent tool_result).
        status = b.status
      } else if (priorResult) {
        // User already acted on this tool_use in a previous turn - preserve
        // the outcome so the button reflects history rather than being
        // re-clickable.
        status = priorResult.isError ? 'denied' : 'navigated'
      } else {
        // No matching tool_result was ever recorded. If we still have a
        // resolvedPath on disk the user can pick up where they left off.
        status = resolvedPath ? 'pending' : 'denied'
      }
      out.push({
        type: 'tool_use',
        id: b.id,
        name: b.name,
        input: (b.input as Record<string, unknown>) ?? {},
        resolvedPath,
        status,
      })
    } else if (b.type === 'tool_result' && typeof b.tool_use_id === 'string') {
      out.push({
        type: 'tool_result',
        tool_use_id: b.tool_use_id,
        content: typeof b.content === 'string' ? b.content : '',
        is_error: !!b.is_error,
      })
    }
  }
  return out
}

const normaliseReaction = (raw: unknown): AssistantReaction => {
  if (raw === 'LIKE' || raw === 'DISLIKE') return raw
  return null
}

const hydrateMessages = (raw: PersistedDbMessage[]): AssistantMessage[] => {
  const acted = indexToolResultsById(raw)
  return raw.map((m) => ({
    id: m.id,
    role: (m.role === 'assistant' ? 'assistant' : 'user') as AssistantRole,
    blocks: hydrateBlocks(m.blocks, acted),
    reaction: normaliseReaction(m.reaction),
  }))
}

interface SuccessEnvelope<T> {
  status?: boolean
  data?: T
  message?: string
}

export function useAssistant() {
  const messagesRef = useRef<AssistantMessage[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const conversationIdRef = useRef<string | null>(null)
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [banner, setBanner] = useState<AssistantBanner | null>(null)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])

  conversationIdRef.current = conversationId
  messagesRef.current = messages

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setMessages([])
    setConversationId(null)
    setBanner(null)
    setIsStreaming(false)
  }, [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setIsStreaming(false)
  }, [])

  const refreshConversations = useCallback(async () => {
    try {
      const resp = await PrivateAxiosInstance.get<SuccessEnvelope<ConversationSummary[]>>(CONVERSATIONS_URL)
      if (resp.data?.data) setConversations(resp.data.data)
    } catch {
      /* non-fatal */
    }
  }, [])

  const loadConversation = useCallback(async (id: string) => {
    try {
      const resp = await PrivateAxiosInstance.get<SuccessEnvelope<{ id: string; messages: PersistedDbMessage[] }>>(
        `${CONVERSATIONS_URL}/${id}`
      )
      const data = resp.data?.data
      if (!data) return
      setConversationId(data.id)
      setMessages(hydrateMessages(data.messages))
      setBanner(null)
    } catch {
      setBanner({ code: 'unknown', message: 'Could not load that conversation.' })
    }
  }, [])

  const deleteConversation = useCallback(async (id: string) => {
    await PrivateAxiosInstance.delete(`${CONVERSATIONS_URL}/${id}`)
    setConversations((prev) => prev.filter((c) => c.id !== id))
    if (conversationIdRef.current === id) {
      setConversationId(null)
      setMessages([])
    }
  }, [])

  // Initial fetch when the hook mounts.
  useEffect(() => {
    void refreshConversations()
  }, [refreshConversations])

  const dismissBanner = useCallback(() => setBanner(null), [])

  /**
   * Run the streaming POST and append events to the placeholder assistant
   * message identified by `assistantId`. The server is the source of truth
   * for prior turns - we only ship the new user turn.
   */
  const runStream = useCallback(
    async (newUserBlocks: AssistantContentBlock[], assistantId: string, currentPath?: string) => {
      setIsStreaming(true)
      setBanner(null)
      const controller = new AbortController()
      abortRef.current = controller

      try {
        const formData = {
          conversationId: conversationIdRef.current,
          message: {
            role: 'user' as const,
            blocks: newUserBlocks.map(stripBlockStateFields),
          },
          currentPath,
        }
        const response = await PrivateAxiosInstance.post(STREAM_URL, formData, {
          signal: controller.signal,
          headers: {
            Accept: 'text/event-stream',
          },
          responseType: 'stream',
          adapter: 'fetch',
        })

        if (response.status < 200 || response.status >= 300 || !response.data)
          throw new Error(`Assistant request failed: ${response.status}`)

        const reader = (response.data as ReadableStream<Uint8Array>).getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let receivedDone = false

        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lastSep = buffer.lastIndexOf('\n\n')
          if (lastSep === -1) continue
          const ready = buffer.slice(0, lastSep + 2)
          buffer = buffer.slice(lastSep + 2)

          parseSseChunk(ready, (event, data) => {
            if (event === 'text') {
              try {
                const { text: delta } = JSON.parse(data) as { text: string }
                setMessages((prev) =>
                  prev.map((m) => {
                    if (m.id !== assistantId) return m
                    const blocks = [...m.blocks]
                    const last = blocks[blocks.length - 1]
                    if (last && last.type === 'text') blocks[blocks.length - 1] = { ...last, text: last.text + delta }
                    else blocks.push({ type: 'text', text: delta })

                    return { ...m, blocks, pending: false }
                  })
                )
              } catch {
                /* ignore */
              }
            } else if (event === 'tool_use') {
              try {
                const payload = JSON.parse(data) as {
                  id: string
                  name: string
                  input: Record<string, unknown>
                  resolvedPath?: string
                }
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? {
                          ...m,
                          pending: false,
                          blocks: [
                            ...m.blocks,
                            {
                              type: 'tool_use',
                              id: payload.id,
                              name: payload.name,
                              input: payload.input,
                              resolvedPath: payload.resolvedPath,
                              status: 'pending',
                            },
                          ],
                        }
                      : m
                  )
                )
              } catch {
                /* ignore */
              }
            } else if (event === 'done') {
              receivedDone = true
              try {
                const payload = JSON.parse(data) as { conversationId?: string }
                if (payload.conversationId) {
                  setConversationId(payload.conversationId)
                  conversationIdRef.current = payload.conversationId
                }
              } catch {
                /* ignore */
              }
            } else if (event === 'error') {
              try {
                const payload = JSON.parse(data) as { message: string; code?: AssistantErrorCode }
                if (payload.code === 'quota_exceeded')
                  setBanner({
                    code: 'quota_exceeded',
                    message:
                      'Your organisation has reached its monthly AI token limit. Try again next month, or ask an admin to raise the limit.',
                  })
                else setBanner({ code: payload.code ?? 'unknown', message: payload.message })

                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? {
                          ...m,
                          pending: false,
                          errored: true,
                          blocks: m.blocks.length > 0 ? m.blocks : [{ type: 'text', text: payload.message }],
                        }
                      : m
                  )
                )
              } catch {
                /* ignore */
              }
            }
          })
        }

        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, pending: false } : m)))

        // Refresh the conversation list after a successful turn so the
        // dropdown reflects the new title / updatedAt.
        if (receivedDone) void refreshConversations()
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return
        let updatedAssistant = false
        setMessages((prev) => {
          updatedAssistant = prev.some((m) => m.id === assistantId)
          if (!updatedAssistant) return prev
          return prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  pending: false,
                  errored: true,
                  blocks: [{ type: 'text', text: 'Sorry, something went wrong. Please try again.' }],
                }
              : m
          )
        })
        if (!updatedAssistant) setBanner({ code: 'unknown', message: 'Sorry, something went wrong. Please try again.' })
      } finally {
        setIsStreaming(false)
        abortRef.current = null
      }
    },
    [refreshConversations]
  )

  const sendMessage = useCallback(
    async ({ text, currentPath }: SendMessageParams) => {
      if (!text.trim() || isStreaming) return

      const userMsg: AssistantMessage = {
        id: newId(),
        role: 'user',
        blocks: [{ type: 'text', text }],
      }
      const assistantMsg: AssistantMessage = {
        id: newId(),
        role: 'assistant',
        blocks: [],
        pending: true,
      }

      setMessages((prev) => [...prev, userMsg, assistantMsg])
      await runStream(userMsg.blocks, assistantMsg.id, currentPath)
    },
    [isStreaming, runStream]
  )

  /**
   * Toggle / set a like|dislike reaction on a persisted assistant message.
   * Optimistically updates local state, then PATCHes the server. On failure,
   * we revert and surface a generic banner.
   */
  const setReaction = useCallback(async (messageId: string, next: AssistantReaction) => {
    let prev: AssistantReaction = null
    setMessages((current) => {
      const target = current.find((m) => m.id === messageId)
      prev = target?.reaction ?? null
      return current.map((m) => (m.id === messageId ? { ...m, reaction: next } : m))
    })
    try {
      await PrivateAxiosInstance.patch(MESSAGE_REACTION_URL(messageId), { reaction: next })
    } catch {
      setMessages((current) => current.map((m) => (m.id === messageId ? { ...m, reaction: prev } : m)))
      setBanner({ code: 'unknown', message: 'Could not save your reaction. Please try again.' })
    }
  }, [])

  const dispatchToolUse = useCallback(
    async ({ toolUseId, navigate, currentPath }: DispatchToolUseParams) => {
      let target: AssistantMessage | undefined
      let block: Extract<AssistantContentBlock, { type: 'tool_use' }> | undefined
      for (const m of messagesRef.current) {
        const found = m.blocks.find(
          (b): b is Extract<AssistantContentBlock, { type: 'tool_use' }> => b.type === 'tool_use' && b.id === toolUseId
        )
        if (found) {
          target = m
          block = found
          break
        }
      }
      if (!target || !block) return
      if (block.status !== 'pending') return

      const path = block.resolvedPath
      let resultText: string
      let isError = false

      if (!path) {
        resultText = 'Could not resolve the destination path.'
        isError = true
      } else {
        let queryEntries: [string, string][] = []
        if (block.name === 'navigate') queryEntries = navigateInputToQueryEntries(block.input)
        else if (block.name === 'open_add_modal') queryEntries = openAddModalInputToQueryEntries(block.input)
        const fullPath = `${path}${buildQueryString(queryEntries)}`
        try {
          navigate(fullPath)
          resultText = block.name === 'open_add_modal' ? `Opened the add form on ${path}.` : `Navigated to ${fullPath}.`
        } catch (e) {
          resultText = `Action failed: ${e instanceof Error ? e.message : 'unknown error'}`
          isError = true
        }
      }

      const newStatus: 'navigated' | 'denied' = isError ? 'denied' : 'navigated'
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== target!.id) return m
          return {
            ...m,
            blocks: m.blocks.map((b) =>
              b.type === 'tool_use' && b.id === toolUseId ? { ...b, status: newStatus } : b
            ),
          }
        })
      )

      const toolResultMsg: AssistantMessage = {
        id: newId(),
        role: 'user',
        blocks: [
          {
            type: 'tool_result',
            tool_use_id: toolUseId,
            content: resultText,
            is_error: isError,
          },
        ],
      }

      // If the user already continued the chat after this offer, the
      // tool_use is no longer adjacent to this result. Persist the click
      // (so the button stays "Opened") but do not start a follow-up turn -
      // the backend would strip the result and Anthropic would 400 on empty
      // user content.
      const targetIndex = messagesRef.current.findIndex((m) => m.id === target.id)
      const isStale =
        targetIndex >= 0 &&
        messagesRef.current
          .slice(targetIndex + 1)
          .some((m) => !m.pending && (m.role === 'user' || m.role === 'assistant'))

      if (isStale) {
        await runStream(toolResultMsg.blocks, newId(), currentPath)
        return
      }

      const assistantMsg: AssistantMessage = {
        id: newId(),
        role: 'assistant',
        blocks: [],
        pending: true,
      }

      setMessages((prev) => [...prev, toolResultMsg, assistantMsg])
      await runStream(toolResultMsg.blocks, assistantMsg.id, currentPath)
    },
    [runStream]
  )

  return {
    messages,
    isStreaming,
    banner,
    dismissBanner,
    conversationId,
    conversations,
    refreshConversations,
    loadConversation,
    deleteConversation,
    sendMessage,
    stop,
    reset,
    dispatchToolUse,
    setReaction,
  }
}
